<template>
  <banners>
    <div
      class="container-max relative mx-auto box-border h-full min-h-screen w-full lg:px-10"
    >
      <div class="relative mx-6 mb-4 mt-8 flex items-center">
        <router-link
          :to="{ name: 'Rate Confirmations' }"
          class="absolute -left-5 top-3"
        >
          <img
            class="mr-2 h-4 w-4 cursor-pointer"
            src="@/assets/back-icon-gray.svg"
            alt="back icon"
            @click="handleBack"
          />
        </router-link>

        <div class="flex w-full items-center justify-between">
          <div
            ref="topElement"
            class="flex w-full items-center justify-between gap-2 overflow-hidden capitalize md:justify-start lg:w-auto"
          >
            <h2
              v-if="rateDetail?.subject"
              class="truncate whitespace-nowrap text-3xl text-dark-gray"
            >
              {{ isMobile ? 'Rate Cons' : rateDetail?.subject }}
            </h2>
            <h2
              v-else
              class="whitespace-nowrap text-3xl text-dark-gray"
            >
              {{ isMobile ? 'Rate Cons' : 'No Subject' }}
            </h2>

            <span
              class="mr-4 rounded-md bg-blue-100 px-2 text-sm font-medium capitalize text-blue-800"
              :class="{
                'bg-green-100 !text-green-800':
                  rateDetail?.status === 'accepted' ||
                  (rateDetail?.terms_accepted_at &&
                    rateDetail?.status !== 'rejected'),
                'bg-red-100 text-danger-default':
                  rateDetail?.status === 'cancelled' &&
                  rateDetail?.cancelled_at,
                '!bg-red-100 text-danger-default':
                  rateDetail?.status === 'rejected' && rateDetail?.rejected_at,
              }"
              >{{ rateStatus }}</span
            >
          </div>
          <div
            v-if="
              rateDetail?.status !== 'cancelled' &&
              rateDetail?.status !== 'rejected'
            "
            class="hidden min-w-max gap-2 lg:flex"
          >
            <ButtonMain
              v-if="canShowRejectedButton"
              class="!py-2 px-4"
              variant="blue"
              @click="openRejectModal"
            >
              Reject
            </ButtonMain>
            <ButtonMain
              v-if="!rateConLink && canShowDownloadButton"
              class="!py-2 px-4"
              variant="gray"
              @click="downloadAttachment"
            >
              Download PDF
            </ButtonMain>
            <ButtonMain
              v-if="!isBrokerUser && !rateDetail?.terms_accepted_at"
              class="!py-1 px-4"
              variant="yellow-black"
              @click="handleTermsModal"
            >
              Accept Terms
            </ButtonMain>
          </div>
        </div>
      </div>

      <div
        class="border border-placeholder-gray bg-gray-100 p-4 lg:border-none lg:bg-white"
      >
        <div class="mt-0 min-h-screen md:mt-4">
          <div
            v-if="isLoading"
            class="flex h-96 justify-center bg-off-white"
          >
            <div
              class="inner-container flex flex-col items-start justify-center p-8 py-16"
            >
              <div class="mx-auto h-16 w-16 text-center">
                <img
                  class="-top-2px loading relative w-full"
                  src="@/assets/loading-v2-yellow.svg"
                  alt=""
                />
              </div>
              <div class="mt-4 text-dark-gray">
                <h3 class="text-center text-xl">
                  Loading rate confirmation details...
                </h3>
              </div>
              <div class="mx-auto mt-4 h-32 text-center">
                <div
                  class="mx-auto w-full text-center text-sm text-gray-over-dark"
                >
                  {{ loadingMessage }}
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-if="!rateDetail">
              <div
                class="inner-container flex flex-col items-center justify-center bg-off-white p-8"
              >
                <div class="magnify-image">
                  <img
                    class="-top-2px relative w-full text-yellow-500"
                    src="@/assets/magnify.svg"
                    alt=""
                  />
                </div>
                <div
                  class="mt-4 flex flex-col items-center justify-center text-gray-over-dark"
                >
                  <h3 class="text-center">
                    It looks like there hasn't been any activity on this rate
                    confirmation.
                  </h3>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-left"
            >
              <h4
                v-if="isMobile"
                class="mb-8 text-lg text-dark-gray lg:text-xl"
              >
                {{ rateDetail?.subject }}
              </h4>

              <div
                class="grid min-h-screen grid-cols-5 gap-4 px-2 md:px-0"
                :class="{ 'grid-cols-7': isBrokerUser }"
              >
                <div
                  class="col-span-5 flex flex-col gap-6 pb-32 lg:col-span-2 lg:pb-0"
                >
                  <div
                    v-for="rate in rateDetail?.recent_rate_confirmation_activities"
                    :key="rate.id"
                    class="flex flex-col text-dark-gray"
                    :class="{
                      'rounded-lg border border-placeholder-gray bg-white py-3':
                        rate.is_type === 'delivered' ||
                        rate.is_type === 'cancelled' ||
                        rate.is_type === 'rejected' ||
                        isBrokerUser,
                      'pt-2': isBrokerUser,
                    }"
                  >
                    <div
                      v-if="
                        rate.is_type === 'delivered' ||
                        rate.is_type == 'cancelled' ||
                        rate.is_type == 'rejected'
                      "
                    >
                      <div
                        class="border-b border-placeholder-gray px-4 pb-3"
                        :class="{
                          'flex items-center justify-between !pb-2':
                            isBrokerUser,
                        }"
                      >
                        <div class="flex gap-2">
                          <img
                            v-if="rate.is_type === 'cancelled'"
                            src="@/assets/rate-con-cancelled.svg"
                            alt="Cancelled icon"
                          />
                          <img
                            v-if="rate.is_type === 'rejected'"
                            src="@/assets/rate-con-rejected.svg"
                            alt="Rejected icon"
                          />
                          <img
                            v-else
                            src="@/assets/rate-con-created.svg"
                            alt="Accepted icon"
                          />
                          <h4 class="text-xl capitalize">
                            {{ rateType(rate.is_type) }}
                          </h4>
                        </div>
                        <div
                          v-if="isBrokerUser"
                          class="flex flex-col items-end"
                        >
                          <span class="text-xs text-neutral-subtlest"
                            >{{ getTimeElapsed(rate?.occurred_at) }} ago</span
                          >
                          <span class="text-xs text-neutral-subtlest"
                            >{{
                              dayjs(rate?.occured_at).format(
                                'h:mm a on M/D/YYYY'
                              )
                            }}
                            ago</span
                          >
                        </div>
                        <span
                          v-else
                          class="text-xs lowercase text-body-color"
                          >{{ getTimeElapsed(rate?.occurred_at) }} ago</span
                        >
                      </div>

                      <div class="flex flex-col gap-1 px-4 pt-2">
                        <div class="flex items-center justify-between">
                          <p class="text-neutral-subtlest">User</p>
                          <p class="text-neutral-subtle-bold">
                            {{ rate?.user?.full_name }}
                          </p>
                        </div>

                        <div class="flex items-center justify-between">
                          <p class="text-neutral-subtlest">Company</p>
                          <p class="text-neutral-subtle-bold">
                            {{ rate?.user?.company?.legal_name }}
                          </p>
                        </div>

                        <div class="flex items-center justify-between">
                          <p class="text-neutral-subtlest">Email</p>
                          <a
                            class="text-sm text-neutral-subtle-yellow underline"
                            :href="`mailto:${rate?.user?.email}`"
                          >
                            {{ rate?.user?.email }}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      :class="{ 'ml-4': !isBrokerUser }"
                    >
                      <div
                        :class="{
                          'flex items-center justify-between px-4 pb-1':
                            isBrokerUser,
                        }"
                      >
                        <div class="flex gap-2">
                          <img
                            v-if="rate.is_type === 'terms_accepted'"
                            src="@/assets/rate-con-accepted.svg"
                            alt="Accepted icon"
                          />
                          <img
                            v-if="rate.is_type === 'downloaded'"
                            src="@/assets/rate-con-downloaded.svg"
                            alt="Accepted icon"
                          />
                          <img
                            v-if="rate.is_type === 'viewed'"
                            src="@/assets/rate-con-viewed.svg"
                            alt="Accepted icon"
                          />
                          <img
                            v-if="rate.is_type === 'rejected'"
                            src="@/assets/rate-con-rejected.svg"
                            alt="Rejected icon"
                          />
                          <h4 class="capitalize">
                            {{ rateType(rate.is_type) }}
                          </h4>
                        </div>
                        <div
                          v-if="isBrokerUser"
                          class="flex flex-col items-end"
                        >
                          <span class="text-xs text-neutral-subtlest"
                            >{{ getTimeElapsed(rate?.occurred_at) }} ago</span
                          >
                          <span class="text-right text-xs text-neutral-subtlest"
                            >{{
                              dayjs(rate?.occured_at).format(
                                'h:mm a on M/D/YYYY'
                              )
                            }}
                            ago</span
                          >
                        </div>
                        <div
                          v-else
                          class="ml-8"
                        >
                          <span class="text-xs capitalize text-body-color">
                            {{ capitalize(rate?.user?.full_name) }} •
                          </span>
                          <span class="text-xs lowercase text-body-color">
                            {{ getTimeElapsed(rate?.occurred_at) }} ago</span
                          >
                        </div>
                      </div>
                      <div
                        v-if="isBrokerUser"
                        class="flex flex-col gap-1 border-t border-placeholder-gray px-4 pt-2"
                      >
                        <div class="flex items-center justify-between">
                          <p class="text-neutral-subtlest">User</p>
                          <p class="text-neutral-subtle-bold">
                            {{ rate?.user?.full_name }}
                          </p>
                        </div>

                        <div class="flex items-center justify-between">
                          <p class="text-neutral-subtlest">Email</p>
                          <a
                            class="text-sm text-neutral-subtle-yellow underline"
                            :href="`mailto:${rate?.user?.email}`"
                          >
                            {{ rate?.user?.email }}
                          </a>
                        </div>

                        <div class="flex items-center justify-between">
                          <p class="text-neutral-subtlest">Phone</p>
                          <p class="text-neutral-subtle-bold">
                            {{
                              formatPhoneParenthesis(rate?.user?.phone_e164) ||
                              '-'
                            }}
                          </p>
                        </div>

                        <div class="flex items-start justify-between">
                          <p class="text-neutral-subtlest">Remote Address</p>
                          <div>
                            <p class="text-right text-neutral-subtle-bold">
                              {{ rate?.remote_address?.ip }}
                            </p>
                            <p class="text-neutral-subtle-bold">
                              {{ formatRemoteAddress(rate?.remote_address) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <ButtonMain
                    v-if="
                      !rateConLink &&
                      (rateDetail?.status !== 'cancelled' ||
                        rateDetail?.status !== 'rejected')
                    "
                    variant="blue"
                    class="!py-4 px-4 lg:hidden"
                    @click="downloadAttachment"
                  >
                    Download
                  </ButtonMain>
                </div>

                <div class="col-span-3 -mt-6 hidden lg:block">
                  <div
                    v-if="rateDetail?.status === 'cancelled'"
                    class="flex w-full items-center justify-center rounded-md bg-dark-gray bg-opacity-70"
                    style="height: calc(100vh - 12rem) !important"
                  >
                    <div
                      class="flex h-20 items-center justify-center gap-3 rounded-md bg-white px-8 shadow-xl"
                    >
                      <svg
                        class="h-8 w-8 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div
                        class="text-body-color"
                        :class="{ 'text-transparent': rateConLink }"
                      >
                        No attachments found
                      </div>
                    </div>
                  </div>
                  <PdfViewer
                    v-else
                    :rate-con-link="rateConLink"
                  />
                </div>

                <div
                  v-if="isBrokerUser"
                  class="col-span-2 flex h-fit w-full max-w-[600px] gap-2 rounded-lg bg-yellow-10 p-4 text-start"
                >
                  <img
                    class="mt-1 h-6 w-6"
                    src="@/assets/yellow-question-mark.svg"
                    alt="question mark"
                  />

                  <div class="flex flex-col gap-2">
                    <p class="text-base">
                      Protect your rate confirmations from bad actors
                    </p>
                    <p class="text-xs text-neutral-subtle">
                      Secure Rate Confirmation Delivery prevents sending
                      sensitive load info via email, stopping unauthorized
                      access and cargo theft.
                    </p>

                    <ButtonMain
                      variant="tertiary"
                      class="mt-2 !w-fit bg-gray-100 !px-3 !py-2 !text-black"
                      @click="handleRateConLearnMore"
                    >
                      Learn More
                    </ButtonMain>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="
          rateDetail?.status !== 'cancelled' &&
          rateDetail?.status !== 'rejected'
        "
        class="fixed bottom-0 left-0 right-0 flex w-full flex-col gap-2 gap-4 bg-white px-4 py-6 shadow-md lg:hidden"
      >
        <ButtonMain
          v-if="viewRateConButtonLink"
          variant="gray"
          class="flex-1 !py-4 px-4"
          @click="viewRateCon"
        >
          View Rate Con
        </ButtonMain>
        <div class="flex gap-4">
          <ButtonMain
            v-if="canShowRejectedButton"
            variant="blue"
            class="flex-1 !py-4 px-4"
            @click="openRejectModal"
          >
            Reject
          </ButtonMain>

          <ButtonMain
            v-if="!isBrokerUser && !rateDetail?.terms_accepted_at"
            class="flex-1 !py-4 px-4"
            variant="yellow-black"
            @click="handleTermsModal"
          >
            Accept Terms
          </ButtonMain>
        </div>
      </div>
    </div>
    <terms-modal
      v-if="showTermsModal"
      :visible="showTermsModal"
      :terms="rateTerms"
      :terms-rate="rateDetail"
      @close="onCloseTermsModal"
      @accept-terms="acceptTerms"
    />
    <reject-rate-con-modal
      v-if="isRejectModalVisible"
      :visible="isRejectModalVisible"
      :loading="rejectRateConLoading"
      @close="closeRejectModal"
      @reject="onRejectRateCon"
    />
  </banners>
</template>

<script>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import {
  formatE164Phones,
  formatPhoneParenthesis,
  capitalize,
} from '@/common/validators';
import {
  getTimeElapsed,
  timestampDate,
  timestampTime,
} from '@/common/timestamps.js';
import Banners from '@/components/Banners.vue';
import PdfViewer from '@/components/PdfViewer.vue';
import TermsModal from '@/components/modals/TermsModal.vue';
import RejectRateConModal from '@/components/modals/RejectRateConModal.vue';
import ButtonMain from '@/components/ButtonMain.vue';
import { useScreenSize } from '@/composables/useScreenSize';

export default {
  name: 'RateConfirmationShow',
  components: {
    Banners,
    PdfViewer,
    ButtonMain,
    TermsModal,
    RejectRateConModal,
  },
  data() {
    return {
      certPagesIndex: 0,
    };
  },
  methods: {
    async downloadAttachment(openInTab) {
      const response = await this.$store.dispatch(
        'rates/downloadRateAttachment',
        { id: this.$route?.params?.id, attachment_index: this.certPagesIndex }
      );
      if (response?.data) {
        const blob = new Blob([response.data]);

        if (openInTab) {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
        } else {
          const contentDisposition = response.headers['content-disposition'];
          const filename = contentDisposition.match(
            /filename\*=UTF-8''(.+)/
          )[1];
          const decodedFilename = decodeURIComponent(filename);
          // IE doesn't support download attribute on anchor tags, so check if browser is IE or not
          if (window?.navigator && window?.navigator?.msSaveOrOpenBlob) {
            // If IE, use proprietary msSaveOrOpenBlob method
            window?.navigator?.msSaveOrOpenBlob(blob, decodedFilename);
          } else {
            // If not IE, create a temporary link element, simulate click, then remove
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', decodedFilename);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        }
      }
    },
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const toast = useToast();

    const rateDetail = computed(() => store.state.rates.current_rate);
    const rateConLink = computed(
      () => rateDetail.value?.rate_confirmation_link
    );
    const rateTerms = computed(
      () => store.state.rates.current_rate_terms?.terms
    );

    const { screenSize } = useScreenSize();

    const isMobile = computed(() => screenSize.value === 'mobile');

    const isBrokerUser = computed(() => false);

    const downloadPdfLink = computed(
      () => rateDetail.value?.attachments?.[0]?.url
    );

    const viewRateConButtonLink = computed(
      () => rateConLink.value || downloadPdfLink.value
    );

    const viewRateCon = () => {
      window.open(viewRateConButtonLink.value, '_blank');
    };

    const isRejectModalVisible = ref(false);
    const rejectRateConLoading = ref(false);

    const canShowRejectedButton = computed(
      () =>
        store.state.users.featureFlags?.view_rate_con_reject?.enabled &&
        rateDetail?.value?.status !== 'cancelled' &&
        rateDetail?.value?.status !== 'rejected' &&
        rateDetail?.value?.status !== 'accepted'
    );

    const canShowDownloadButton = computed(
      () => rateDetail?.value?.status === 'accepted'
    );

    const openRejectModal = () => {
      isRejectModalVisible.value = true;
    };

    const closeRejectModal = () => {
      isRejectModalVisible.value = false;
    };

    const onRejectRateCon = async () => {
      rejectRateConLoading.value = true;
      const id = Number(router?.currentRoute?.value?.params?.id);

      try {
        const { status } = await store.dispatch(
          'rates/rejectRateConfirmation',
          id
        );

        const messages = {
          200: {
            msg: 'Rate confirmation successfully rejected',
            type: 'success',
          },
          201: {
            msg: 'Rate confirmation successfully rejected',
            type: 'success',
          },
          403: { msg: 'Something happened. Try again later.', type: 'error' },
          422: {
            msg: 'Validation error. Please refresh and try again.',
            type: 'error',
          },
        };

        const { msg, type } = messages[status] || {
          msg: 'Unexpected response. Try again later.',
          type: 'error',
        };

        toast.open({
          message: msg,
          type,
          position: 'top-right',
          duration: 2500,
        });

        if (status === 200 || status === 201) {
          await store.dispatch('rates/getRateDetail', id);
        }
      } catch (error) {
        toast.open({
          message: 'Failed to reject rate confirmation. Try again.',
          type: 'error',
          position: 'top-right',
          duration: 2500,
        });
        console.error(error);
      } finally {
        rejectRateConLoading.value = false;
        isRejectModalVisible.value = false;
      }
    };

    const rateType = (type) => {
      if (type === 'terms_accepted') {
        return 'Accepted';
      } else if (type === 'delivered') {
        return 'Created';
      } else {
        return type;
      }
    };

    const rateStatus = computed(() => {
      if (rateDetail?.value?.status === 'rejected') {
        return 'Rejected';
      } else if (rateDetail?.value?.terms_accepted_at) {
        return 'Accepted';
      } else if (rateDetail?.value?.status === 'delivered') {
        return 'new';
      } else {
        return rateDetail?.value?.status;
      }
    });

    const formatRemoteAddress = (address) => {
      return `${address?.city_name}, ${address?.region_name} (${address?.country_code}) ${address?.zip_code}`;
    };

    const handleRateConLearnMore = () => {
      window.open(
        'https://help.highway.com/en/articles/10910720-secure-rate-confirmation-delivery',
        '_blank'
      );

      return;
    };

    // const canRateCon = computed(() => { return store.state.users?.currentUser?.paywall?.can_rate_confirmation})

    onBeforeMount(async () => {
      isLoading.value = true;
      // if (canRateCon.value) {
      if (!rateDetail.value?.id) {
        const rate_detail = await store.dispatch(
          'rates/getRateDetail',
          parseInt(router?.currentRoute?.value?.params?.id)
        );
        await store.dispatch(
          'rates/getRateTerms',
          parseInt(router?.currentRoute?.value?.params?.id)
        );

        await store.dispatch(
          'loads/getCarrierVehicles',
          rate_detail?.carrier?.id
        );
      }
      // }
      isLoading.value = false;
    });

    onBeforeUnmount(() => {
      store.commit('rates/setCurrentRate', null);
      store.commit('rates/setCurrentRateTerms', null);
    });

    let pageTabs = ref(['Activity Timeline', 'Status']);
    const searchText = ref('');
    const topElement = ref(null);
    let activePageTab = ref('Activity Timeline');
    const loadingMessage = ref('Loading...');

    const isLoading = ref(false);

    const setActivePageTab = (tab) => {
      searchText.value = '';
      activePageTab.value = tab;
    };

    // const resolveUrl = (carrier) => {
    //   const route =  router.resolve({
    //     name: 'CarriersShow',
    //     params: { id: carrier?.id }
    //   });

    //   return route.href
    // }

    const showTermsModal = ref(false);

    const openTermsModal = () => {
      showTermsModal.value = true;
    };

    const onCloseTermsModal = () => {
      showTermsModal.value = false;
    };

    const acceptTerms = async () => {
      const id = router?.currentRoute?.value?.params?.id;

      try {
        const res = await store.dispatch('rates/acceptRateTerms', id);

        if ([200, 201, 204].includes(res?.status)) {
          toast?.open({
            type: 'success',
            message: 'Terms accepted',
            position: 'top-right',
            duration: 5000,
          });

          await store.dispatch('rates/getRateDetail', parseInt(id));
        }
      } catch (error) {
        console.log('error: ', error);

        toast?.open({
          type: 'error',
          message: 'An error occurred. Please try again.',
          position: 'top-right',
          duration: 5000,
        });
      }
    };

    const handleTermsModal = () => {
      if (!rateTerms?.value) {
        acceptTerms();
      } else {
        openTermsModal();
      }
    };

    const carrierHasAccepted = (rate) => {
      if (rate?.recent_rate_confirmation_activities?.length > 0) {
        return rate?.recent_rate_confirmation_activities?.some(
          (r) => r?.is_type == 'terms_accepted'
        );
      } else {
        return (
          rate?.rate_confirmation_activities?.some(
            (r) => r?.is_type == 'terms_accepted'
          ) || false
        );
      }
    };

    return {
      viewRateConButtonLink,
      rateConLink,
      rateDetail,
      rateTerms,
      dayjs,
      // canRateCon,
      isBrokerUser,
      isLoading,
      topElement,
      pageTabs,
      acceptTerms,
      activePageTab,
      loadingMessage,
      setActivePageTab,
      // resolveUrl,
      formatE164Phones,
      formatPhoneParenthesis,
      getTimeElapsed,
      timestampDate,
      timestampTime,
      showTermsModal,
      onCloseTermsModal,
      handleTermsModal,
      carrierHasAccepted,
      rateType,
      rateStatus,
      isMobile,
      capitalize,
      formatRemoteAddress,
      handleRateConLearnMore,
      viewRateCon,
      isRejectModalVisible,
      rejectRateConLoading,
      canShowRejectedButton,
      canShowDownloadButton,
      openRejectModal,
      closeRejectModal,
      onRejectRateCon,
    };
  },
};
</script>

<style scoped>
.container-pills {
  width: 15.5%;
}
</style>
